import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { useFPContext } from '../context/FuturePathContext';
import { Scale as A1, AlertTriangle as A2, Wallet as A3, Clock as A4, Sparkles as A5, Target as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, DollarSign, Briefcase, AlertTriangle, CheckCircle,
  Target, Clock, MapPin, Users, Lightbulb, BarChart3, Shield,
  ChevronRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RealityPlanPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const { traits } = useFPContext();
  const [realityPlan, setRealityPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchRealityPlan();
  }, []);

  const fetchRealityPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reality-plan/${user.id}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setRealityPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch reality plan:', error);
      toast.error('Failed to load reality plan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!realityPlan) {
    return (
      <AppLayout>
        <PageHeader title="Reality Plan" description="Your personalized career reality check" />
        <GlassCard>
          <p className="text-slate-300">Unable to generate your reality plan. Please complete your profile first.</p>
        </GlassCard>
      </AppLayout>
    );
  }

  const { job_market_signals, career_outcomes, life_path_projection } = realityPlan;
  const marketSignal = job_market_signals?.[0];
  const careerOutcome = career_outcomes?.[0];

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="reality-arrival"
        eyebrow="Scenario Lab"
        title="What happens if life happens?"
        subtitle={`Plans break on the things nobody planned for. Run yours against ${traits?.has_career ? 'a lost job' : 'months without income'}, a rent rise, a year off or a move, and find out what survives.`}
        accent="#818cf8"
        second="#f472b6"
        ctaLabel="Stress-test my plan"
        narration={"This is the Scenario Lab. Every plan looks fine until something happens, so we test yours against the things that actually happen: losing income for a few months, rent rising, a year out, a move, or a family obligation arriving early. You will see which parts hold, which break, and the small changes now that make the whole thing survivable."}
        facts={[
          { icon: A2, head: "Real shocks", sub: traits?.has_career ? "Job loss, rent rises, a year out, a move" : "Lost income, rent rises, a year out, a move" },
          { icon: A1, head: "What holds", sub: "Which parts of the plan survive" },
          { icon: A6, head: "What to change", sub: "Small moves now that buy resilience" }
        ]}
        modules={[
          { icon: A3, label: "Income shock", line: "A few months without pay, modelled", hue: "#818cf8" },
          { icon: A4, label: "Delay", line: "What a year off costs, and what it does not", hue: "#fbbf24" },
          { icon: A2, label: "Cost rises", line: "Rent, fuel and interest moving against you", hue: "#f472b6" },
          { icon: A6, label: "Family change", line: "Obligations arriving sooner than planned", hue: "#34d399" },
          { icon: A1, label: "Verdict", line: "Fragile, workable or solid, and why", hue: "#22d3ee" },
          { icon: A5, label: "Fixes", line: "The cheapest ways to make it hold", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Reality Plan"
        description="Your personalized career and life path projection"
      />

      {/* Reality Score Banner */}
      <GlassCard className="mb-8 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/30">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold ${
              realityPlan.reality_score >= 70 ? 'bg-green-500/20 text-green-400' :
              realityPlan.reality_score >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {Math.round(realityPlan.reality_score)}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Reality Score</h2>
              <p className="text-slate-300">How achievable is your plan?</p>
            </div>
          </div>
          <div className="flex gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-white">{realityPlan.confidence_level}</p>
              <p className="text-sm text-slate-300">Confidence</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{realityPlan.primary_path_type}</p>
              <p className="text-sm text-slate-300">Path Type</p>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Target },
          { id: 'market', label: 'Job Market', icon: TrendingUp },
          { id: 'income', label: 'Income Projections', icon: DollarSign },
          { id: 'life', label: 'Life Path', icon: Clock },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-800 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Your Strengths
            </h3>
            <ul className="space-y-2">
              {realityPlan.strengths_for_path?.map((strength, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  {strength}
                </li>
              ))}
            </ul>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Gaps to Address
            </h3>
            <ul className="space-y-2">
              {realityPlan.gaps_to_address?.map((gap, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300">
                  <div className="w-2 h-2 rounded-full bg-yellow-400" />
                  {gap}
                </li>
              ))}
            </ul>
          </GlassCard>

          <GlassCard className="md:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-blue-400" />
              Recommended Actions
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {realityPlan.recommended_actions?.map((action, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-navy-800/50 rounded-lg">
                  <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </span>
                  <p className="text-slate-300">{action}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          {realityPlan.risk_warnings?.length > 0 && (
            <GlassCard className="md:col-span-2 bg-red-900/10 border-red-500/30">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Risk Warnings
              </h3>
              <ul className="space-y-2">
                {realityPlan.risk_warnings.map((warning, i) => (
                  <li key={i} className="flex items-center gap-2 text-red-300">
                    <AlertTriangle className="w-4 h-4" />
                    {warning}
                  </li>
                ))}
              </ul>
            </GlassCard>
          )}
        </div>
      )}

      {/* Job Market Tab */}
      {activeTab === 'market' && marketSignal && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Market Overview</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Outlook</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  marketSignal.outlook === 'hot' ? 'bg-green-500/20 text-green-400' :
                  marketSignal.outlook === 'stable' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {marketSignal.outlook?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Growth Rate</span>
                <span className="text-emerald-400 font-semibold">+{marketSignal.growth_rate}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Monthly Openings</span>
                <span className="text-white font-semibold">{marketSignal.openings_monthly?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Automation Risk</span>
                <span className={`font-semibold ${
                  marketSignal.automation_risk < 0.2 ? 'text-green-400' :
                  marketSignal.automation_risk < 0.4 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {Math.round(marketSignal.automation_risk * 100)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Remote Availability</span>
                <span className="text-white font-semibold">{Math.round(marketSignal.remote_availability * 100)}%</span>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Geographic Hotspots</h3>
            <div className="flex flex-wrap gap-2">
              {marketSignal.geographic_hotspots?.map((city, i) => (
                <span key={i} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {city}
                </span>
              ))}
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Required Skills</h3>
            <div className="flex flex-wrap gap-2">
              {marketSignal.required_skills?.map((skill, i) => (
                <span key={i} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Emerging Skills</h3>
            <div className="flex flex-wrap gap-2">
              {marketSignal.emerging_skills?.map((skill, i) => (
                <span key={i} className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="md:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4">Top Employers</h3>
            <div className="flex flex-wrap gap-3">
              {marketSignal.top_employers?.map((employer, i) => (
                <span key={i} className="px-4 py-2 bg-navy-800 text-white rounded-lg border border-slate-700">
                  {employer}
                </span>
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {/* Income Projections Tab */}
      {activeTab === 'income' && careerOutcome && (
        <div className="space-y-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Lifetime Earnings Estimate</h3>
            <div className="text-center py-6">
              <p className="text-5xl font-bold text-emerald-400">
                ${(careerOutcome.lifetime_earnings_estimate / 1000000).toFixed(1)}M
              </p>
              <p className="text-slate-300 mt-2">Over 40-year career</p>
            </div>
          </GlassCard>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {careerOutcome.income_projections?.map((proj, i) => (
              <GlassCard key={i}>
                <h4 className="text-sm text-slate-300 mb-2">{proj.period?.toUpperCase()}</h4>
                <p className="text-xs text-slate-300 mb-3">{proj.years_experience} years</p>
                <p className="text-2xl font-bold text-white">${proj.salary_median?.toLocaleString()}</p>
                <p className="text-sm text-slate-300">{proj.typical_title}</p>
                <div className="mt-3 text-xs text-slate-300">
                  <p>Range: ${proj.salary_low?.toLocaleString()} - ${proj.salary_high?.toLocaleString()}</p>
                </div>
              </GlassCard>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <GlassCard className="text-center">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold text-white">{careerOutcome.job_satisfaction_score}/10</p>
              <p className="text-sm text-slate-300">Job Satisfaction</p>
            </GlassCard>
            <GlassCard className="text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-2xl font-bold text-white">{careerOutcome.work_life_balance_score}/10</p>
              <p className="text-sm text-slate-300">Work-Life Balance</p>
            </GlassCard>
            <GlassCard className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold text-white capitalize">{careerOutcome.advancement_potential}</p>
              <p className="text-sm text-slate-300">Advancement Potential</p>
            </GlassCard>
          </div>
        </div>
      )}

      {/* Life Path Tab */}
      {activeTab === 'life' && life_path_projection && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <GlassCard className="text-center">
              <p className="text-sm text-slate-300 mb-2">Education Cost</p>
              <p className="text-3xl font-bold text-red-400">${life_path_projection.education_cost?.toLocaleString()}</p>
            </GlassCard>
            <GlassCard className="text-center">
              <p className="text-sm text-slate-300 mb-2">Debt at Graduation</p>
              <p className="text-3xl font-bold text-amber-400">${life_path_projection.debt_at_graduation?.toLocaleString()}</p>
            </GlassCard>
            <GlassCard className="text-center">
              <p className="text-sm text-slate-300 mb-2">Years to Debt Freedom</p>
              <p className="text-3xl font-bold text-blue-400">{life_path_projection.years_to_debt_freedom} years</p>
            </GlassCard>
          </div>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Net Worth Projections</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-sm text-slate-300 mb-1">At Age 30</p>
                <p className="text-2xl font-bold text-emerald-400">${life_path_projection.net_worth_at_30?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-slate-300 mb-1">At Age 40</p>
                <p className="text-2xl font-bold text-emerald-400">${life_path_projection.net_worth_at_40?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-slate-300 mb-1">{traits?.adult === false ? 'In your sixties' : 'At Retirement'}</p>
                <p className="text-2xl font-bold text-emerald-400">${life_path_projection.net_worth_at_retirement?.toLocaleString()}</p>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Life Milestones</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-white font-semibold">First Job</p>
                  <p className="text-slate-300">Age {life_path_projection.expected_first_job_age}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-white font-semibold">Home Purchase</p>
                  <p className="text-slate-300">Age {life_path_projection.expected_home_purchase_age}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Target className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-white font-semibold">Financial Independence</p>
                  <p className="text-slate-300">Age {life_path_projection.expected_financial_independence_age}</p>
                </div>
              </div>
            </div>
          </GlassCard>

          <div className="grid md:grid-cols-3 gap-4">
            <GlassCard>
              <h4 className="text-sm text-slate-300 mb-2">Career Flexibility</h4>
              <p className="text-xl font-bold text-white capitalize">{life_path_projection.career_flexibility}</p>
            </GlassCard>
            <GlassCard>
              <h4 className="text-sm text-slate-300 mb-2">Geographic Mobility</h4>
              <p className="text-xl font-bold text-white capitalize">{life_path_projection.geographic_mobility}</p>
            </GlassCard>
            <GlassCard>
              <h4 className="text-sm text-slate-300 mb-2">Industry Stability</h4>
              <p className="text-xl font-bold text-white capitalize">{life_path_projection.industry_stability}</p>
            </GlassCard>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
